'use client';

import type { ReactNode } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { FiTrash2 } from 'react-icons/fi';

import type { ModalProps } from '../modal';
import { Text } from '../text';
import Title from '../title/title';
import { openConfirmPrompt } from './open-confirm-prompt';

interface UseConfirmDeleteModalProps {
  icon?: ReactNode;
  title?: string;
  children?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ModalProps['variant'];
}

export interface HandleConfirmDeleteProps {
  confirmationMessage?: ReactNode | string;
  onConfirm: (() => void) | (() => Promise<void>);
  /** Called when its cancel button is pressed, but not when it's dismissed some other way. */
  onCancel?: () => void;
  /** Called however the prompt closes, after `onConfirm` or `onCancel` when either was pressed. */
  onClose?: () => void;
}

/**
 * {@link useConfirmModal} for a destructive action: trash icon, red confirm button, and a default
 * "this can't be undone" body.
 */
export const useConfirmDeleteModal = (props: UseConfirmDeleteModalProps) => {
  const { icon, title, children, confirmText, cancelText, variant } = props;
  const { t } = useTranslation();

  const confirmDelete = (handlers: HandleConfirmDeleteProps) =>
    openConfirmPrompt(
      {
        icon: icon ?? <FiTrash2 color={'var(--mantine-color-error)'} />,
        defaultTitle: <Title order={5}>{t('DELETE: Are you sure?')}</Title>,
        defaultBody: <Text>{t('This action cannot be undone')}</Text>,
        confirmLabel: confirmText ?? t('Delete'),
        cancelLabel: cancelText ?? t('Cancel'),
        confirmColor: 'red',
        variant,
      },
      { ...handlers, children, title },
    );

  return {
    confirmDelete,
  };
};
